/**
 * Modul Fidelizare pe client / tabletă / slug public.
 */
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';
import { getPublicClientSlug } from '../services/public-client.js?v=83.21';
import { getBucharestDateKey } from '../utils/helpers.js?v=83.21';
import {
  registerLoyaltyPurchaseBackend,
  checkLoyaltyProgressBackend
} from '../services/backend.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import {
  DEFAULT_LOYALTY_BUTTON,
  DEFAULT_LOYALTY_DESCRIPTION,
  DEFAULT_LOYALTY_TITLE
} from './loyalty-module-admin.js?v=83.21';
import { DEFAULT_EVENT_BUTTON_LABEL } from './event-module-admin.js?v=83.21';
import { isEventVisible } from './event-module.js?v=83.21';
import { isCustomTemplateActive, isAvvaTemplateActive } from './client-template-style.js?v=83.21';
import { loyaltyHeadline, loyaltyKicker } from '../utils/loyalty-text.js?v=83.21';

const DEFAULT_CTA_ICON_HTML =
  '<svg viewBox="0 0 24 24" class="client-premium-svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8V5"/><path d="M7.5 8c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"/><path d="M12 13v3"/><path d="M10.5 15.5h3"/></svg>';

let detachOverlayListeners = null;
let overlayKeyHandler = null;
let isSubmittingLoyalty = false;
let isCheckingLoyaltyProgress = false;

function getLoyaltyFromSettings(settings) {
  return settings?.loyaltySettings || null;
}

export function isLoyaltyVisible(settings) {
  const ls = getLoyaltyFromSettings(settings);
  if (!ls || ls.active !== true) return false;
  if (settings?.campaignStatus === 'active') return false;
  const start = String(ls.startDate || '').trim();
  const today = getBucharestDateKey();
  if (start && today < start) return false;
  const end = String(ls.endDate || '').trim();
  if (end && today > end) return false;
  return true;
}

const LOYALTY_BTN_GRADIENT = 'linear-gradient(135deg, #f59e0b 0%, #d97706 55%, #b45309 100%)';

/* Textele puse automat de asistentul de campanii, pe tipuri. Dacă în setări a rămas textul altui tip
 * (ex. „Răzuiește!” de la o campanie veche, iar acum e o campanie cu extragere), folosim textul tipului curent. */
const WIZARD_BUTTON_TEXT = { scratch: 'Răzuiește!', guaranteed: 'Ia-ți cadoul!', tombola: 'Înscrie-te!' };
const WIZARD_BUTTON_TEXTS = new Set(Object.values(WIZARD_BUTTON_TEXT));
export function campaignButtonLabel(settings) {
  const kind = String(settings?.campaignKind || '').trim();
  const own = WIZARD_BUTTON_TEXT[kind] || '';
  const txt = String(settings?.buttonText || '').trim();
  if (!txt) return own || 'Apasă!';
  if (own && WIZARD_BUTTON_TEXTS.has(txt) && txt !== own) return own;
  return txt;
}

/** Propagă buttonColor din setări — CSS template folosește var(--campaign-btn-bg). */
export function syncCampaignButtonColor(settings) {
  const page = document.getElementById('clientPage');
  const playBtn = document.getElementById('playBtn');
  // Avva / Afiș / Tablă / Glass impun culoarea CTA din CSS — nu lăsa buttonColor inline să o suprascrie
  if (page?.matches('.template-avva, .template-afis, .template-tabla, .template-glass')) {
    page.style.removeProperty('--campaign-btn-bg');
    if (playBtn) playBtn.style.removeProperty('background');
    return;
  }
  const color = String(settings?.buttonColor || '').trim();
  if (page) {
    if (color) page.style.setProperty('--campaign-btn-bg', color);
    else page.style.removeProperty('--campaign-btn-bg');
  }
  if (playBtn && color) {
    playBtn.style.setProperty('background', color, 'important');
  }
}

/** Kicker „Card de fidelitate” deasupra textului din butonul mare (Universal Soft etc.). */
function setPlayBtnKicker(text) {
  const playBtn = document.getElementById('playBtn');
  const wrap = playBtn?.querySelector('.client-premium-cta__text');
  if (!wrap) return;
  let k = wrap.querySelector('.sj-loy-kicker');
  if (!text) {
    k?.remove();
    delete playBtn.dataset.loyCard;
    return;
  }
  if (!k) {
    k = document.createElement('span');
    k.className = 'sj-loy-kicker';
    wrap.insertBefore(k, wrap.firstChild);
  }
  k.textContent = text;
  playBtn.dataset.loyCard = '1';
}

function setPlayBtnLabel(text) {
  const playBtn = document.getElementById('playBtn');
  if (!playBtn) return;
  const labelEl = playBtn.querySelector('.client-premium-cta__label');
  if (labelEl) labelEl.textContent = text;
  else playBtn.innerText = text;
}

function setPlayBtnIcon(playBtn, mode) {
  const iconEl = playBtn?.querySelector('.client-premium-cta__icon');
  if (!iconEl) return;
  if (mode === 'event') {
    iconEl.innerHTML = '<span class="client-premium-cta__emoji" aria-hidden="true">🎉</span>';
  } else {
    iconEl.innerHTML = DEFAULT_CTA_ICON_HTML;
  }
}

/** Butonul mare central (#playBtn) — același stil ca campania. */
export function updateClientPrimaryActionSlots(settings) {
  if (isCustomTemplateActive()) return;

  const campaignBlock = document.getElementById('layoutBlockCampaign');
  const playBtn = document.getElementById('playBtn');
  const rulesBlock = document.getElementById('layoutBlockRules');
  const triesCounter = document.getElementById('triesCounter');
  const centerLoyalty = document.getElementById('clientLoyaltyCenterBtn');

  const status = settings?.campaignStatus || 'draft';
  const campaignActive = status === 'active';
  const loyaltyActive = isLoyaltyVisible(settings);
  const eventActive = isEventVisible(settings);
  const ls = getLoyaltyFromSettings(settings) || {};
  const loyaltyLabel = String(ls.buttonText || '').trim() || DEFAULT_LOYALTY_BUTTON;
  const eventLabel =
    String(settings?.eventModule?.buttonLabel || '').trim() || DEFAULT_EVENT_BUTTON_LABEL;

  if (centerLoyalty) centerLoyalty.style.display = 'none';

  const showCenterSlot = campaignActive || loyaltyActive || eventActive;
  if (campaignBlock) {
    if (showCenterSlot) {
      campaignBlock.style.setProperty('display', 'flex', 'important');
      campaignBlock.style.setProperty('align-items', 'center', 'important');
      campaignBlock.style.setProperty('justify-content', 'center', 'important');
      campaignBlock.removeAttribute('aria-hidden');
    } else {
      campaignBlock.style.setProperty('display', 'none', 'important');
      campaignBlock.setAttribute('aria-hidden', 'true');
    }
  }

  if (playBtn) {
    if (campaignActive) {
      playBtn.style.setProperty('display', 'flex', 'important');
      playBtn.setAttribute('data-action', 'playGame');
      playBtn.setAttribute('aria-haspopup', 'false');
      playBtn.removeAttribute('aria-controls');
      playBtn.disabled = false;
      playBtn.classList.remove('disabled', 'client-premium-cta--event');
      setPlayBtnLabel(campaignButtonLabel(settings));
      setPlayBtnKicker('');
      setPlayBtnIcon(playBtn, 'default');
      playBtn.style.background = settings?.buttonColor || '';
      playBtn.style.opacity = '1';
      playBtn.style.pointerEvents = 'auto';
      playBtn.style.cursor = 'pointer';
      playBtn.style.animation = '';
    } else if (loyaltyActive) {
      playBtn.style.setProperty('display', 'flex', 'important');
      playBtn.setAttribute('data-action', 'openLoyaltyModuleOverlay');
      playBtn.setAttribute('aria-haspopup', 'dialog');
      playBtn.setAttribute('aria-controls', 'loyaltyModuleOverlay');
      playBtn.disabled = false;
      playBtn.classList.remove('disabled', 'client-premium-cta--event');
      setPlayBtnLabel(loyaltyHeadline(ls));
      setPlayBtnKicker(loyaltyKicker(ls));
      setPlayBtnIcon(playBtn, 'default');
      syncCampaignButtonColor(settings);
      if (!String(settings?.buttonColor || '').trim() && !isAvvaTemplateActive() && !document.getElementById('clientPage')?.matches('.template-afis, .template-tabla, .template-glass')) {
        playBtn.style.setProperty('background', LOYALTY_BTN_GRADIENT, 'important');
      }
      playBtn.style.opacity = '1';
      playBtn.style.pointerEvents = 'auto';
      playBtn.style.cursor = 'pointer';
      playBtn.style.animation = '';
    } else if (eventActive) {
      playBtn.style.setProperty('display', 'flex', 'important');
      playBtn.setAttribute('data-action', 'openEventModuleOverlay');
      playBtn.setAttribute('aria-haspopup', 'dialog');
      playBtn.setAttribute('aria-controls', 'eventModuleOverlay');
      playBtn.disabled = false;
      playBtn.classList.remove('disabled');
      playBtn.classList.add('client-premium-cta--event');
      setPlayBtnLabel(eventLabel);
      setPlayBtnKicker('');
      setPlayBtnIcon(playBtn, 'event');
      playBtn.style.background = '';
      playBtn.style.opacity = '1';
      playBtn.style.pointerEvents = 'auto';
      playBtn.style.cursor = 'pointer';
      playBtn.style.animation = '';
    } else {
      playBtn.style.setProperty('display', 'none', 'important');
      playBtn.disabled = true;
      playBtn.classList.remove('client-premium-cta--event');
      playBtn.style.pointerEvents = 'none';
    }
  }

  if (rulesBlock) {
    const showRules = campaignActive;
    rulesBlock.style.display = showRules ? '' : 'none';
    rulesBlock.setAttribute('aria-hidden', showRules ? 'false' : 'true');
  }

  if (triesCounter) {
    if (!campaignActive) triesCounter.textContent = '';
  }

  const page = document.getElementById('clientPage');
  const campaignSub = document.getElementById('clientCampaignSubtitle');
  const primaryKind = campaignActive ? 'campaign' : loyaltyActive ? 'loyalty' : eventActive ? 'event' : 'none';

  if (page) {
    page.dataset.primaryModule = primaryKind;
  }

  if (playBtn) {
    playBtn.classList.toggle('client-premium-cta--loyalty', loyaltyActive);
    playBtn.dataset.primaryModule = primaryKind;
  }

  if (campaignSub) {
    if (campaignActive) {
      const msg = String(settings?.claimMessage || '').trim() || 'Joacă și câștigă premii!';
      campaignSub.textContent = msg;
      campaignSub.hidden = false;
      campaignSub.removeAttribute('hidden');
      campaignSub.style.removeProperty('display');
    } else if (loyaltyActive) {
      // titlul spune deja recompensa — aici doar acțiunea (fără repetiții)
      const desc = ls.mode === 'ranking' ? '1 vizită = 1 punct · scanează bonul' : 'Scanează bonul la fiecare vizită';
      campaignSub.textContent = desc;
      campaignSub.hidden = !desc;
      campaignSub.style.display = desc ? '' : 'none';
    } else if (eventActive) {
      const desc = String(settings?.eventModule?.text || settings?.eventModule?.title || '').trim();
      campaignSub.textContent = desc;
      campaignSub.hidden = !desc;
      campaignSub.style.display = desc ? '' : 'none';
    } else {
      campaignSub.textContent = '';
      campaignSub.hidden = true;
      campaignSub.setAttribute('hidden', '');
      campaignSub.style.display = 'none';
    }
  }
}

function closeOverlayShell(overlay, trigger) {
  if (!overlay) return;
  overlay.setAttribute('hidden', '');
  overlay.classList.remove('is-open');
  overlay.setAttribute('aria-hidden', 'true');
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('loyalty-module-open');
  resetLoyaltyForm();
}

function resetLoyaltyForm() {
  const form = document.getElementById('loyaltyPurchaseForm');
  const email = document.getElementById('loyaltyEmailInput');
  const receipt = document.getElementById('loyaltyReceiptInput');
  const result = document.getElementById('loyaltyResultBox');
  const progressEmail = document.getElementById('loyaltyProgressEmailInput');
  const progressResult = document.getElementById('loyaltyProgressResultBox');
  if (form) form.hidden = false;
  if (email) email.value = '';
  if (receipt) receipt.value = '';
  if (result) {
    result.hidden = true;
    result.innerHTML = '';
  }
  if (progressEmail) progressEmail.value = '';
  if (progressResult) {
    progressResult.hidden = true;
    progressResult.innerHTML = '';
  }
}

function renderProgressResultBox(res) {
  const box = document.getElementById('loyaltyProgressResultBox');
  if (!box) return;
  const lines = [`<p class="loyalty-result__progress">${res.progressMessage || ''}</p>`];
  if (res.validationHint) {
    lines.push(`<p class="loyalty-result__hint">${res.validationHint}</p>`);
  }
  box.innerHTML = lines.join('');
  box.hidden = false;
}

function resolveLoyaltySource() {
  const slug = getPublicClientSlug();
  if (slug) return 'public';
  const menuMode = document.getElementById('clientMenuMode');
  if (menuMode && !menuMode.hidden) return 'tablet';
  return 'tablet';
}

export function openLoyaltyModuleOverlay() {
  const overlay = document.getElementById('loyaltyModuleOverlay');
  const trigger = document.getElementById('clientLoyaltyModuleTrigger');
  if (!overlay) return;
  overlay.removeAttribute('hidden');
  overlay.classList.add('is-open');
  overlay.setAttribute('aria-hidden', 'false');
  if (trigger) trigger.setAttribute('aria-expanded', 'true');
  document.body.classList.add('loyalty-module-open');
  resetLoyaltyForm();
  import('./loyalty-scan.js?v=83.21')
    .then((m) => m.mountLoyaltyScan?.())
    .catch(() => {});
}

export function closeLoyaltyModuleOverlay() {
  const overlay = document.getElementById('loyaltyModuleOverlay');
  const trigger = document.getElementById('clientLoyaltyModuleTrigger');
  closeOverlayShell(overlay, trigger);
}

function isValidEmailClient(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export async function checkLoyaltyProgress() {
  if (isCheckingLoyaltyProgress) return;
  const progressEmail = document.getElementById('loyaltyProgressEmailInput');
  const purchaseEmail = document.getElementById('loyaltyEmailInput');
  const email = String(progressEmail?.value || purchaseEmail?.value || '').trim();

  if (!email) {
    showSnackbar('Introdu adresa de email pentru verificare.', 'error');
    return;
  }
  if (!isValidEmailClient(email)) {
    showSnackbar('Introdu un email valid.', 'error');
    return;
  }

  isCheckingLoyaltyProgress = true;
  const btn = document.getElementById('loyaltyCheckProgressBtn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Se verifică...';
  }

  try {
    const res = await checkLoyaltyProgressBackend({
      email,
      publicSlug: getPublicClientSlug() || null
    });
    if (progressEmail && !progressEmail.value && purchaseEmail?.value) {
      progressEmail.value = purchaseEmail.value;
    }
    renderProgressResultBox(res);
  } catch (e) {
    showSnackbar(e?.message || 'Nu am putut verifica progresul.', 'error');
  } finally {
    isCheckingLoyaltyProgress = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Verifică progresul';
    }
  }
}

export async function submitLoyaltyPurchase() {
  if (isSubmittingLoyalty) return;
  const email = String(document.getElementById('loyaltyEmailInput')?.value || '').trim();
  const receiptNumber = String(document.getElementById('loyaltyReceiptInput')?.value || '').trim();

  if (!email) {
    showSnackbar('Introdu adresa de email.', 'error');
    return;
  }
  if (!isValidEmailClient(email)) {
    showSnackbar('Introdu un email valid.', 'error');
    return;
  }
  if (receiptNumber.length < 3) {
    showSnackbar('Numărul bonului trebuie să aibă cel puțin 3 caractere.', 'error');
    return;
  }

  isSubmittingLoyalty = true;
  const submitBtn = document.getElementById('loyaltySubmitBtn');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Se înregistrează...';
  }

  try {
    const res = await registerLoyaltyPurchaseBackend({
      email,
      receiptNumber,
      publicSlug: getPublicClientSlug() || null,
      source: resolveLoyaltySource()
    });

    const result = document.getElementById('loyaltyResultBox');
    const form = document.getElementById('loyaltyPurchaseForm');
    if (form) form.hidden = true;
    if (result) {
      result.hidden = false;
      const lines = [
        `<p class="loyalty-result__success">${res.message || 'Achiziția a fost înregistrată.'}</p>`
      ];
      if (res.progressMessage) {
        lines.push(`<p class="loyalty-result__progress">${res.progressMessage}</p>`);
      }
      if (res.validationHint) {
        lines.push(`<p class="loyalty-result__hint">${res.validationHint}</p>`);
      }
      result.innerHTML = lines.join('');
    }
    const progressEmail = document.getElementById('loyaltyProgressEmailInput');
    if (progressEmail && email) progressEmail.value = email;
    const progressResult = document.getElementById('loyaltyProgressResultBox');
    if (progressResult && res.progressMessage) {
      renderProgressResultBox(res);
    }
  } catch (e) {
    const msg = e?.message || 'Nu am putut înregistra achiziția.';
    showSnackbar(msg, 'error');
  } finally {
    isSubmittingLoyalty = false;
    if (submitBtn) {
      submitBtn.disabled = false;
      const settings = window.__currentSettingsForLoyalty;
      const btnLabel =
        String(settings?.loyaltySettings?.buttonText || '').trim() || DEFAULT_LOYALTY_BUTTON;
      submitBtn.textContent = btnLabel;
    }
  }
}

export function applyLoyaltyModuleUI(settings) {
  if (isCustomTemplateActive()) return;

  window.__currentSettingsForLoyalty = settings;

  if (detachOverlayListeners) {
    try {
      detachOverlayListeners();
    } catch (_) {
      /* ignore */
    }
    detachOverlayListeners = null;
  }
  if (overlayKeyHandler) {
    document.removeEventListener('keydown', overlayKeyHandler);
    overlayKeyHandler = null;
  }

  const wrap = document.getElementById('layoutBlockLoyalty');
  const trigger = document.getElementById('clientLoyaltyModuleTrigger');
  const triggerLabel = document.getElementById('clientLoyaltyModuleTriggerLabel');
  const menuBtn = document.getElementById('clientMenuLoyaltyModuleBtn');
  const overlay = document.getElementById('loyaltyModuleOverlay');
  const overlayTitle = document.getElementById('loyaltyModuleOverlayTitle');
  const overlayDesc = document.getElementById('loyaltyModuleOverlayDescription');
  const overlayReward = document.getElementById('loyaltyModuleOverlayReward');
  const overlayPromo = document.getElementById('loyaltyModuleOverlayPromo');
  const submitBtn = document.getElementById('loyaltySubmitBtn');
  const closeBtn = document.getElementById('loyaltyModuleOverlayClose');
  const backdrop = document.getElementById('loyaltyModuleOverlayBackdrop');

  const active = isLoyaltyVisible(settings);
  const ls = getLoyaltyFromSettings(settings) || {};
  const buttonLabel = String(ls.buttonText || '').trim() || DEFAULT_LOYALTY_BUTTON;
  const title = String(ls.title || '').trim() || DEFAULT_LOYALTY_TITLE;
  const description = String(ls.description || '').trim() || DEFAULT_LOYALTY_DESCRIPTION;
  const rewardText = String(ls.rewardText || '').trim();
  const promoUrl = sanitizeImageSrc(ls.promoImageUrl || '');

  if (wrap) {
    wrap.style.display = 'none';
    wrap.setAttribute('aria-hidden', 'true');
  }
  if (trigger) trigger.style.display = 'none';
  if (triggerLabel) triggerLabel.textContent = buttonLabel;
  if (menuBtn) menuBtn.style.display = active ? '' : 'none';

  if (overlayTitle) overlayTitle.textContent = title;
  if (overlayDesc) overlayDesc.textContent = description;
  if (overlayReward) {
    overlayReward.textContent = rewardText;
    overlayReward.hidden = !rewardText;
  }
  if (submitBtn) submitBtn.textContent = buttonLabel;

  if (overlayPromo) {
    if (promoUrl) {
      overlayPromo.innerHTML = `<img src="${promoUrl}" alt="" class="loyalty-overlay-promo__img" />`;
      overlayPromo.style.display = '';
    } else {
      overlayPromo.innerHTML = '';
      overlayPromo.style.display = 'none';
    }
  }

  updateClientPrimaryActionSlots(settings);

  if (!active && overlay) {
    closeLoyaltyModuleOverlay();
  }

  if (active && overlay && closeBtn && backdrop) {
    const onClose = () => closeLoyaltyModuleOverlay();
    closeBtn.addEventListener('click', onClose);
    backdrop.addEventListener('click', onClose);
    overlayKeyHandler = (ev) => {
      if (ev.key === 'Escape') closeLoyaltyModuleOverlay();
    };
    document.addEventListener('keydown', overlayKeyHandler);
    detachOverlayListeners = () => {
      closeBtn.removeEventListener('click', onClose);
      backdrop.removeEventListener('click', onClose);
    };
  }
}
